import { type AppState, Auth0Provider } from '@auth0/auth0-react'
import { type ReactNode } from 'react'

import { router } from '~/shell/App'
import { resolveReturnTo } from '~/shell/auth/resolveReturnTo'
import { getAuthConfig } from '~/shell/config'

interface Auth0ProviderWithConfigProps {
  children: ReactNode
}

/**
 * Returns the user to where they were before Universal Login (AUTH-05).
 *
 * `redirect_uri` is the bare origin (Auth0 allows only a fixed callback URL), so the
 * pre-login location travels in `appState.returnTo` instead. Auth0 sends the user back to
 * `/`, which would otherwise fall through `Bootstrap` to the default customer's first
 * category; navigating the data router to the resolved `returnTo` here — before auth0-react
 * flips `isAuthenticated` and the guarded tree renders — lands them on their original URL.
 * The default callback's `history.replaceState` is invisible to a `createBrowserRouter` data
 * router, so it must be `router.navigate`.
 */
const onRedirectCallback = (appState?: AppState) => {
  void router.navigate(resolveReturnTo(appState))
}

/**
 * Wraps its children in an `Auth0Provider` configured from runtime config.
 *
 * Reads the Auth0 domain, client ID, and audience via `getAuthConfig()`, which
 * throws `RegardConfigError` when the domain or client ID is missing — making a
 * misconfigured boot fail fast before the React tree renders (AUTH-02). Auth0 returns the
 * user to `window.location.origin`, and `onRedirectCallback` then navigates on to the
 * location they were at before logging in (AUTH-05).
 *
 * Sessions persist across reloads via `cacheLocation="localstorage"` +
 * `useRefreshTokens`; auth0's default in-memory cache re-prompts login on every
 * refresh.
 */
export const Auth0ProviderWithConfig = ({
  children,
}: Auth0ProviderWithConfigProps) => {
  const { audience, clientId, domain } = getAuthConfig()

  return (
    <Auth0Provider
      authorizationParams={{
        audience,
        redirect_uri: window.location.origin,
      }}
      cacheLocation="localstorage"
      clientId={clientId}
      domain={domain}
      onRedirectCallback={onRedirectCallback}
      useRefreshTokens
    >
      {children}
    </Auth0Provider>
  )
}
